import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  ScaleIcon,
  ArrowRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import HotelRefCard from '../components/HotelRefCard'
import FlightRefCard from '../components/FlightRefCard'
import AuthHeader from '../components/AuthHeader'
import { buildApiUrl } from '../config/api'

function CompareRedemption() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('flights')
  const [flightSearchCriteria, setFlightSearchCriteria] = useState({
    from: '',
    to: '',
    cabinClass: 'economy',
    points: '',
    tripType: 'one_way',
    carrier: '',
    cashValue: '',
    flightNumber: '',
    travelDate: '',
    originAirport: '',
    loyaltyProgram: '',
    segments: [
      {
        origin_iata: '',
        destination_iata: '',
        travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        operating_carrier_id: '',
        flight_number: '',
        cabin_class: 'Economy'
      }
    ]
  })
  const [hotelSearchCriteria, setHotelSearchCriteria] = useState({
    hotelName: '',
    city: '',
    country: '',
    checkInDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    checkOutDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    points: '',
    cashPrice: '',
    loyaltyProgram: '',
    bookingDate: new Date().toISOString().split('T')[0]
  })
  
  // Hotel autocomplete state
  const [showHotelDropdown, setShowHotelDropdown] = useState(false)
  const [filteredHotels, setFilteredHotels] = useState([])
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(-1)
  
  
  const [searchResults, setSearchResults] = useState([])
  const [aggregatedData, setAggregatedData] = useState({
    averageCpp: 0,
    totalRedemptions: 0,
    lowestPoints: 0,
    modePoints: 0
  })
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [globalStats, setGlobalStats] = useState({
    bestRecord: 0,
    totalRedemptions: 0,
    topPrograms: []
  })
  const [globalStatsLoading, setGlobalStatsLoading] = useState(false)
  const [loyaltyPrograms, setLoyaltyPrograms] = useState([])
  const [userRedemptions, setUserRedemptions] = useState([])
  const [userStats, setUserStats] = useState({
    totalRedemptions: 0,
    averageCpp: 0,
    totalPoints: 0
  })
  const [userStatsLoading, setUserStatsLoading] = useState(false)
  const [redemptionReference, setRedemptionReference] = useState(null)
  const [redemptionReferenceLoading, setRedemptionReferenceLoading] = useState(false)
  const [autoFetchedCashPrice, setAutoFetchedCashPrice] = useState(null)

  // Fetch global statistics on component mount and when tab changes
  useEffect(() => {
    fetchGlobalStats()
    fetchLoyaltyPrograms()
  }, [activeTab])

  // Fetch user redemptions when user changes
  useEffect(() => {
    if (user) {
      fetchUserRedemptions()
    }
  }, [user])

  // Reset search results when switching tabs
  useEffect(() => {
    setSearchResults([])
    setHasSearched(false)
    setAggregatedData({
      averageCpp: 0,
      totalRedemptions: 0,
      lowestPoints: 0,
      modePoints: 0
    })
    setRedemptionReference(null)
    // Reset hotel autocomplete state
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
  }, [activeTab])

  // Handle trip type changes and manage segments accordingly
  useEffect(() => {
    if (flightSearchCriteria.tripType === 'round_trip') {
      // Round trip should always have exactly 2 segments
      if (flightSearchCriteria.segments.length !== 2) {
        setFlightSearchCriteria(prev => ({
          ...prev,
          segments: [
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            },
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            }
          ]
        }))
      }
    } else if (flightSearchCriteria.tripType === 'multi_city') {
      // Multi-city should have at least 1 segment
      if (flightSearchCriteria.segments.length === 0) {
        setFlightSearchCriteria(prev => ({
          ...prev,
          segments: [
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            }
          ]
        }))
      }
    } else {
      // One-way trips should have exactly 1 segment
      if (flightSearchCriteria.segments.length !== 1) {
        setFlightSearchCriteria(prev => ({
          ...prev,
          segments: [
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            }
          ]
        }))
      }
    }
  }, [flightSearchCriteria.tripType])

  // Clear auto-fetched cash price when trip type changes
  useEffect(() => {
    if (autoFetchedCashPrice) {
      setAutoFetchedCashPrice(null)
      // Clear cash value if it matches the auto-fetched value (so a new fetch will happen)
      setFlightSearchCriteria(prev => {
        const currentCashValue = parseFloat(prev.cashValue) || 0
        const fetchedPrice = autoFetchedCashPrice.fetched_cash_price || 0
        if (Math.abs(currentCashValue - fetchedPrice) < 0.01) {
          return { ...prev, cashValue: '' }
        }
        return prev
      })
    }
  }, [flightSearchCriteria.tripType])

  const fetchGlobalStats = async () => {
    setGlobalStatsLoading(true)
    try {
      // Pass the current tab type to filter global stats
      const type = activeTab === 'flights' ? 'flight' : 'hotel';
      const response = await fetch(buildApiUrl(`/api/analytics/global-stats?type=${type}`))
      if (response.ok) {
        const data = await response.json()
        setGlobalStats(data)
      } else {
        // console.error('Failed to fetch global stats')
        // Set empty data instead of fake fallback data
        setGlobalStats({
          bestRecord: 0,
          totalRedemptions: 0,
          topPrograms: []
        })
      }
    } catch (error) {
      // console.error('Error fetching global stats:', error)
      // Set empty data on error instead of fake fallback data
      setGlobalStats({
        bestRecord: 0,
        totalRedemptions: 0,
        topPrograms: []
      })
    } finally {
      setGlobalStatsLoading(false)
    }
  }

  // Helper function to get filtered loyalty programs by type
  const getFilteredPrograms = (type) => {
    // Map form redemption types to database program types
    const typeMapping = {
      'flight': 'airline',
      'hotel': 'hotel'
    }
    const mappedType = typeMapping[type] || type
    return loyaltyPrograms.filter(program => program.program_type === mappedType)
  }

  // Fetch loyalty programs from backend
  const fetchLoyaltyPrograms = async () => {
    try {
      // console.log('🔍 Fetching loyalty programs...')
      const response = await fetch(buildApiUrl('/api/loyalty-programs'))
      // console.log('🔍 Response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        // console.log('🔍 Loyalty programs data:', data)
        setLoyaltyPrograms(data.programs || [])
        
        // Set default program to first available flight program
        if (data.programs && data.programs.length > 0) {
          const flightPrograms = getFilteredPrograms('flight')
          // console.log('🔍 Flight programs found:', flightPrograms)
          if (flightPrograms.length > 0) {
            setFlightSearchCriteria(prev => ({
              ...prev,
              loyaltyProgram: flightPrograms[0].id
            }))
          }
          
          // Also set default hotel program
          const hotelPrograms = getFilteredPrograms('hotel')
          // console.log('🔍 Hotel programs found:', hotelPrograms)
          if (hotelPrograms.length > 0) {
            setHotelSearchCriteria(prev => ({
              ...prev,
              loyaltyProgram: hotelPrograms[0].id
            }))
          }
        }
      } else {
        // console.error('❌ Failed to fetch loyalty programs:', response.status, response.statusText)
      }
    } catch (error) {
      // console.error('❌ Error fetching loyalty programs:', error)
    }
  }

  // Fetch user redemptions and calculate stats
  const fetchUserRedemptions = async () => {
    if (!user) return
    
    setUserStatsLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/api/redemptions'), {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        const redemptions = data.redemptions || []
        setUserRedemptions(redemptions)
        
        // Calculate user stats
        if (redemptions.length > 0) {
          const totalPoints = redemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0)
          const cppValues = redemptions
            .map(r => r.value_cpp)
            .filter(cpp => cpp && cpp > 0)
          
          const averageCpp = cppValues.length > 0 
            ? cppValues.reduce((sum, cpp) => sum + cpp, 0) / cppValues.length 
            : 0
          
          setUserStats({
            totalRedemptions: redemptions.length,
            averageCpp: averageCpp,
            totalPoints: totalPoints
          })
        }
      } else {
        // console.error('Failed to fetch user redemptions')
      }
    } catch (error) {
      // console.error('Error fetching user redemptions:', error)
    } finally {
      setUserStatsLoading(false)
    }
  }

  // Fetch redemption reference data for comparison
  const fetchRedemptionReference = async (redemptionData) => {
    setRedemptionReferenceLoading(true)
    try {
      const params = new URLSearchParams({
        redemption_type: redemptionData.redemption_type,
        points_spent: redemptionData.points_spent,
        cash_price: redemptionData.cash_price,
        user_cpp: redemptionData.user_cpp
      })

      // Add route-specific parameters for flights
      if (redemptionData.redemption_type === 'flight') {
        if (redemptionData.origin_iata && redemptionData.destination_iata) {
          params.append('origin_iata', redemptionData.origin_iata)
          params.append('destination_iata', redemptionData.destination_iata)
        }
      }

      // Add hotel-specific parameters for hotels
      if (redemptionData.redemption_type === 'hotel' && redemptionData.hotel_name) {
        params.append('hotel_name', redemptionData.hotel_name)
      }

      const response = await fetch(buildApiUrl(`/api/analytics/redemption-reference?${params}`))
      
      if (response.ok) {
        const data = await response.json()
        setRedemptionReference(data)
      } else {
        // console.error('Failed to fetch redemption reference data')
        setRedemptionReference(null)
      }
    } catch (error) {
      // console.error('Error fetching redemption reference data:', error)
      setRedemptionReference(null)
    } finally {
      setRedemptionReferenceLoading(false)
    }
  }



  // Handle hotel name input change
  const handleHotelNameChange = (value) => {
    setHotelSearchCriteria(prev => ({ ...prev, hotelName: value }))
    
    if (value.trim().length >= 2) {
      fetchHotels(value)
      setShowHotelDropdown(true)
    } else {
      setFilteredHotels([])
      setShowHotelDropdown(false)
    }
    setSelectedHotelIndex(-1)
  }

  // Handle hotel selection from dropdown
  const handleHotelSelect = (hotel) => {
    setHotelSearchCriteria(prev => ({ 
      ...prev, 
      hotelName: hotel.name,
      city: hotel.city,
      country: hotel.country,
      brand: hotel.brand || '',
      hotelGroup: hotel.hotel_group || ''
    }))
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
    
  }

  // Handle keyboard navigation for hotel dropdown
  const handleHotelKeyDown = (e) => {
    if (!showHotelDropdown || filteredHotels.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev < filteredHotels.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev > 0 ? prev - 1 : filteredHotels.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedHotelIndex >= 0 && selectedHotelIndex < filteredHotels.length) {
          handleHotelSelect(filteredHotels[selectedHotelIndex])
        }
        break
      case 'Escape':
        setShowHotelDropdown(false)
        setSelectedHotelIndex(-1)
        break
    }
  }

  const cabinClassOptions = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium_economy', label: 'Premium Economy' },
    { value: 'business', label: 'Business' },
    { value: 'first', label: 'First Class' }
  ]

  const handleFlightSearch = async () => {
    // Validate segments for all trip types
    if (!flightSearchCriteria.segments || flightSearchCriteria.segments.length === 0) {
      alert('Please fill in required fields')
      return
    }
    
    // Check each segment has required fields
    for (let i = 0; i < flightSearchCriteria.segments.length; i++) {
      const segment = flightSearchCriteria.segments[i]
      if (!segment.origin_iata || !segment.destination_iata || !segment.travel_date || !segment.cabin_class) {
        alert('Please fill in all required fields for all flight segments')
        return
      }
    }

    // Check common required fields
    if (!flightSearchCriteria.points || !flightSearchCriteria.loyaltyProgram) {
      alert('Please fill in required fields')
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // Prepare the redemption data for the standard createRedemption endpoint
      const redemptionData = {
        program_id: flightSearchCriteria.loyaltyProgram,
        redemption_type: 'flight',
        points_spent: parseInt(flightSearchCriteria.points),
        cash_price: flightSearchCriteria.cashValue ? parseFloat(flightSearchCriteria.cashValue) : 0,
        award_type: 'standard',
        booking_date: new Date().toISOString().split('T')[0], // Always use current date
        travel_start_date: flightSearchCriteria.segments[0].travel_date,
        return_date: flightSearchCriteria.tripType === 'round_trip' && flightSearchCriteria.segments.length > 1
          ? flightSearchCriteria.segments[1].travel_date
          : null,
        // Flight-specific fields
        trip_type: flightSearchCriteria.tripType,
        num_passengers: 1, // Always set to 1 as requested
        // For one-way flights, include top-level fields (required by backend validation)
        ...(flightSearchCriteria.tripType === 'one_way' && {
          origin_iata: flightSearchCriteria.segments[0].origin_iata,
          destination_iata: flightSearchCriteria.segments[0].destination_iata,
          operating_carrier_id: flightSearchCriteria.segments[0].operating_carrier_id,
          flight_number: flightSearchCriteria.segments[0].flight_number,
          cabin_class: flightSearchCriteria.segments[0].cabin_class
        }),
        // Use segments for all trip types
        segments: flightSearchCriteria.segments.map(segment => ({
          origin_iata: segment.origin_iata,
          destination_iata: segment.destination_iata,
          travel_date: segment.travel_date,
          operating_carrier_id: segment.operating_carrier_id,
          flight_number: segment.flight_number,
          cabin_class: segment.cabin_class
        }))
      }

      // Send the redemption data to the standard createRedemption endpoint
      const headers = {
        'Content-Type': 'application/json',
      }
      
      // Add authorization header if user is logged in
      if (user) {
        const token = localStorage.getItem('token')
        if (token) {
          headers['Authorization'] = 'Bearer ' + token
        }
      }
      
      const response = await fetch(buildApiUrl('/api/redemptions'), {
        method: 'POST',
        headers,
        body: JSON.stringify(redemptionData)
      })

      if (response.ok) {
        const result = await response.json()
        
        // Handle auto-fetched cash price information
        if (result.auto_fetched_cash_price) {
          // Update the cash value in the form if it was auto-fetched
          if (result.auto_fetched_cash_price.fetched_cash_price > 0) {
            setFlightSearchCriteria(prev => ({
              ...prev,
              cashValue: result.auto_fetched_cash_price.fetched_cash_price.toString()
            }));
            // Store auto-fetch information for UI display
            setAutoFetchedCashPrice(result.auto_fetched_cash_price);
          }
        }
        
        // Calculate user's CPP for this redemption
        const userCpp = flightSearchCriteria.cashValue ? 
          (parseFloat(flightSearchCriteria.cashValue) / parseInt(flightSearchCriteria.points) * 100) : 0
        
        // Fetch redemption reference data for comparison
        await fetchRedemptionReference({
          redemption_type: 'flight',
          points_spent: parseInt(flightSearchCriteria.points),
          cash_price: flightSearchCriteria.cashValue ? parseFloat(flightSearchCriteria.cashValue) : 0,
          user_cpp: userCpp,
          origin_iata: flightSearchCriteria.segments && flightSearchCriteria.segments[0] ? flightSearchCriteria.segments[0].origin_iata : '',
          destination_iata: flightSearchCriteria.segments && flightSearchCriteria.segments[0] ? flightSearchCriteria.segments[0].destination_iata : ''
        })
        
        // Search for similar flight redemptions
        const searchParams = new URLSearchParams({
          from: flightSearchCriteria.segments[0].origin_iata,
          to: flightSearchCriteria.segments[0].destination_iata,
          type: 'flight',
          limit: 50
        })
        
        const searchResponse = await fetch(buildApiUrl(`/api/redemptions/search?${searchParams}`))
        
        if (searchResponse.ok) {
          const data = await searchResponse.json()
          
          let filteredResults = data.redemptions || [];
          
          filteredResults = filteredResults.filter(r => r.redemption_type === 'flight' && r.flight);
          
          setSearchResults(filteredResults)
          
          // Calculate aggregated data
          if (filteredResults && filteredResults.length > 0) {
            const cppValues = filteredResults
              .map(r => r.value_cpp)
              .filter(cpp => cpp > 0)
            
            const pointsValues = filteredResults
              .map(r => r.points_spent)
              .filter(points => points > 0)
            
            if (cppValues.length > 0 && pointsValues.length > 0) {
              const avgCpp = cppValues.reduce((sum, cpp) => sum + cpp, 0) / cppValues.length
              const lowestPoints = Math.min(...pointsValues)
              const modePoints = calculateMode(pointsValues)
              
              setAggregatedData({
                averageCpp: avgCpp,
                totalRedemptions: filteredResults.length,
                lowestPoints: lowestPoints,
                modePoints: modePoints
              })
            } else {
              setAggregatedData({
                averageCpp: 0,
                totalRedemptions: filteredResults.length,
                lowestPoints: 0,
                modePoints: 0
              })
            }
          } else {
            setAggregatedData({
              averageCpp: 0,
              totalRedemptions: 0,
              highestPoints: 0,
              lowestPoints: 0,
              modePoints: 0
            })
          }
        } else {
          setSearchResults([])
          setAggregatedData({
            averageCpp: 0,
            totalRedemptions: 0,
            highestPoints: 0,
            lowestPoints: 0,
            modePoints: 0
          })
        }
        
        // Don't reset form immediately - let user see the redemption reference data
        // setHasSearched(false) // Keep this true to show the redemption reference section
      } else {
        const errorData = await response.json()
        alert('Failed to save redemption: ' + (errorData.message || 'Unknown error'))
      }
    } catch (error) {
      console.error('Error creating flight redemption:', error)
      alert('Error saving redemption. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleHotelSearch = async () => {
    // Validate required fields
    if (!hotelSearchCriteria.hotelName || !hotelSearchCriteria.points || !hotelSearchCriteria.loyaltyProgram || !hotelSearchCriteria.city || !hotelSearchCriteria.country) {
      const missingFields = []
      if (!hotelSearchCriteria.hotelName) missingFields.push('Hotel Name')
      if (!hotelSearchCriteria.points) missingFields.push('Points Spent')
      if (!hotelSearchCriteria.loyaltyProgram) missingFields.push('Loyalty Program')
      if (!hotelSearchCriteria.city) missingFields.push('City')
      if (!hotelSearchCriteria.country) missingFields.push('Country')
      
      alert('Please fill in all required fields marked with (*):\n\nMissing: ' + missingFields.join(', ') + '\n\nAll fields with a red asterisk (*) are required.')
      return
    }

    // Validate dates
    if (hotelSearchCriteria.checkOutDate <= hotelSearchCriteria.checkInDate) {
      alert('Check-out date must be after check-in date')
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      // First, create the hotel redemption
      const redemptionData = {
        program_id: hotelSearchCriteria.loyaltyProgram,
        redemption_type: 'hotel',
        points_spent: parseInt(hotelSearchCriteria.points),
        cash_price: hotelSearchCriteria.cashPrice ? parseFloat(hotelSearchCriteria.cashPrice) : 0,
        award_type: 'standard', // Default to standard since field was removed
        booking_date: hotelSearchCriteria.bookingDate,
        // For hotels, travel_start_date should be the check-in date
        travel_start_date: hotelSearchCriteria.checkInDate,
        // Hotel-specific fields
        hotel_name: hotelSearchCriteria.hotelName,
        city: hotelSearchCriteria.city,
        country: hotelSearchCriteria.country,
        check_in_date: hotelSearchCriteria.checkInDate,
        check_out_date: hotelSearchCriteria.checkOutDate,
        room_type: null, // Default to null since field was removed
        brand: null, // Default to null since field was removed
        hotel_group: null // Default to null since field was removed
      }

      // Send the redemption data to the standard createRedemption endpoint
      const headers = {
        'Content-Type': 'application/json',
      }
      
      // Add authorization header if user is logged in
      if (user) {
        const token = localStorage.getItem('token')
        if (token) {
          headers['Authorization'] = 'Bearer ' + token
        }
      }
      
      const response = await fetch(buildApiUrl('/api/redemptions'), {
        method: 'POST',
        headers,
        body: JSON.stringify(redemptionData)
      })

      if (response.ok) {
        const result = await response.json()
        // console.log('✅ Hotel redemption created successfully:', result)
        
        // Calculate user's CPP for this redemption
        const userCpp = hotelSearchCriteria.cashPrice ? 
          (parseFloat(hotelSearchCriteria.cashPrice) / parseInt(hotelSearchCriteria.points) * 100) : 0
        
        // Fetch redemption reference data for comparison
        await fetchRedemptionReference({
          redemption_type: 'hotel',
          points_spent: parseInt(hotelSearchCriteria.points),
          cash_price: hotelSearchCriteria.cashPrice ? parseFloat(hotelSearchCriteria.cashPrice) : 0,
          user_cpp: userCpp,
          hotel_name: hotelSearchCriteria.hotelName
        })
        
        // Search for similar hotel redemptions
        const params = new URLSearchParams({
          to: hotelSearchCriteria.hotelName,
          type: 'hotel',
          limit: '4',
          orderBy: 'created_at',
          sort: 'desc'
        })

        const searchResponse = await fetch(buildApiUrl(`/api/redemptions/search?${params}`))
        
        if (searchResponse.ok) {
          const data = await searchResponse.json()
          // console.log('🔍 Hotel search response:', data);
          
          let filteredResults = data.redemptions || [];
          filteredResults = filteredResults.filter(r => r.redemption_type === 'hotel' && r.hotel);
          
          // console.log('🔍 Filtered hotel results:', filteredResults);
          setSearchResults(filteredResults)
          
          // Calculate aggregated data
          if (filteredResults && filteredResults.length > 0) {
            const cppValues = filteredResults
              .map(r => r.value_cpp)
              .filter(cpp => cpp && cpp > 0)
            
            const pointsValues = filteredResults
              .map(r => r.points_spent)
              .filter(points => points > 0)
            
            if (cppValues.length > 0 && pointsValues.length > 0) {
              const avgCpp = cppValues.reduce((sum, cpp) => sum + cpp, 0) / cppValues.length
              const highestPoints = Math.max(...pointsValues)
              const lowestPoints = Math.min(...pointsValues)
              const modePoints = calculateMode(pointsValues)
              
              setAggregatedData({
                averageCpp: avgCpp,
                totalRedemptions: filteredResults.length,
                highestPoints: highestPoints,
                lowestPoints: lowestPoints,
                modePoints: modePoints
              })
            }
          }
        }
      } else {
        const errorData = await response.json()
        // console.error('❌ Failed to create hotel redemption:', errorData)
                  alert('Failed to save redemption: ' + (errorData.message || 'Unknown error'))
      }
    } catch (error) {
      // console.error('❌ Error creating hotel redemption:', error)
      alert('Error saving redemption. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    if (activeTab === 'flights') {
      // Get default loyalty program
      const flightPrograms = getFilteredPrograms('flight')
      const defaultProgramId = flightPrograms.length > 0 ? flightPrograms[0].id : ''
      
      setFlightSearchCriteria({
        from: '',
        to: '',
        cabinClass: 'economy',
        points: '',
        tripType: 'one_way',
        carrier: '',
        cashValue: '',
        flightNumber: '',
        travelDate: '',
        originAirport: '',
        loyaltyProgram: defaultProgramId,
        segments: [
          {
            origin_iata: '',
            destination_iata: '',
            travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            operating_carrier_id: '',
            flight_number: '',
            cabin_class: 'Economy'
          }
        ]
      })
    } else {
      // Get default loyalty program for hotels
      const hotelPrograms = getFilteredPrograms('hotel')
      const defaultProgramId = hotelPrograms.length > 0 ? hotelPrograms[0].id : ''
      
      setHotelSearchCriteria({
        hotelName: '',
        city: '',
        country: '',
        checkInDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        checkOutDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        points: '',
        cashPrice: '',
        loyaltyProgram: defaultProgramId,
        bookingDate: new Date().toISOString().split('T')[0]
      })
      
      // Reset search states
      setShowHotelDropdown(false)
      setFilteredHotels([])
      setSelectedHotelIndex(-1)
    }
    setSearchResults([])
    setHasSearched(false)
    setAggregatedData({
      averageCpp: 0,
      totalRedemptions: 0,
      lowestPoints: 0,
      modePoints: 0
    })
    setRedemptionReference(null)
  }

  const handleSegmentChange = (index, field, value) => {
    // Clear auto-fetched cash price when fields that affect price fetching change
    const priceAffectingFields = ['origin_iata', 'destination_iata', 'travel_date', 'cabin_class']
    if (priceAffectingFields.includes(field) && autoFetchedCashPrice) {
      setAutoFetchedCashPrice(null)
      // Clear cash value if it matches the auto-fetched value (so a new fetch will happen)
      setFlightSearchCriteria(prev => {
        const currentCashValue = parseFloat(prev.cashValue) || 0
        const fetchedPrice = autoFetchedCashPrice.fetched_cash_price || 0
        if (Math.abs(currentCashValue - fetchedPrice) < 0.01) {
          return { ...prev, cashValue: '' }
        }
        return prev
      })
    }
    
    setFlightSearchCriteria(prev => {
      const newSegments = prev.segments.map((segment, i) => 
        i === index ? { ...segment, [field]: value } : segment
      )
      
      // Auto-fill for round trip: when segment 1 origin/destination changes, update segment 2
      if (prev.tripType === 'round_trip' && index === 0) {
        if (field === 'origin_iata') {
          // When departure origin changes, update return destination
          newSegments[1] = { ...newSegments[1], destination_iata: value }
        } else if (field === 'destination_iata') {
          // When departure destination changes, update return origin
          newSegments[1] = { ...newSegments[1], origin_iata: value }
        }
      }
      
      // Auto-fill for multi-city: when any segment's destination changes, update the next segment's origin
      if (prev.tripType === 'multi_city' && field === 'destination_iata' && index < newSegments.length - 1) {
        newSegments[index + 1] = { ...newSegments[index + 1], origin_iata: value }
      }
      
      // Handle date validation: when a travel date changes, update subsequent segments if needed
      if (field === 'travel_date') {
        for (let i = index + 1; i < newSegments.length; i++) {
          if (newSegments[i].travel_date <= value) {
            // Set subsequent segment date to the day after the current segment date
            const nextDate = new Date(new Date(value).getTime() + 24 * 60 * 60 * 1000)
            newSegments[i] = { 
              ...newSegments[i], 
              travel_date: nextDate.toISOString().split('T')[0] 
            }
          }
        }
      }
      
      return {
        ...prev,
        segments: newSegments
      }
    })
  }

  const addSegment = () => {
    setFlightSearchCriteria(prev => ({
      ...prev,
      segments: [
        ...prev.segments,
        {
          origin_iata: '',
          destination_iata: '',
          travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          operating_carrier_id: '',
          flight_number: '',
          cabin_class: 'Economy'
          }
        ]
      }))
    }

  const removeSegment = (index) => {
    // For round trips, always keep exactly 2 segments
    if (flightSearchCriteria.tripType === 'round_trip') {
      return
    }
    
    // For multi-city, allow removing segments but keep at least 1
    if (flightSearchCriteria.segments.length > 1) {
      setFlightSearchCriteria(prev => ({
        ...prev,
        segments: prev.segments.filter((_, i) => i !== index)
      }))
    }
  }

  const formatCpp = (cpp) => {
    return cpp ? `${cpp.toFixed(2)}¢` : 'N/A'
  }

  const formatCurrency = (amount) => {
    return amount ? `$${parseFloat(amount).toFixed(2)}` : 'N/A'
  }

  // Helper function to calculate mode
  const calculateMode = (values) => {
    if (values.length === 0) return 0
    
    const frequency = {}
    let maxFreq = 0
    let mode = values[0]
    
    values.forEach(value => {
      frequency[value] = (frequency[value] || 0) + 1
      if (frequency[value] > maxFreq) {
        maxFreq = frequency[value]
        mode = value
      }
    })
    
    return mode
  }


  const fetchHotels = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setFilteredHotels([])
      return
    }

    try {
      // Search for existing hotel redemptions to get hotel names with location info
      const response = await fetch(buildApiUrl('/api/redemptions/search?type=hotel&limit=20'))
      if (response.ok) {
        const data = await response.json()
        const hotels = data.redemptions || []
        
        // Filter hotels by name containing search term
        const matchingHotels = hotels
          .filter(redemption => redemption.hotel?.hotel?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(redemption => ({
            id: redemption.hotel.hotel.id,
            name: redemption.hotel.hotel.name,
            city: redemption.hotel.hotel.city,
            country: redemption.hotel.hotel.country,
            brand: redemption.hotel.hotel.brand,
            hotel_group: redemption.hotel.hotel.hotel_group
          }))
          .filter((hotel, index, self) => 
            // Remove duplicates based on hotel name
            index === self.findIndex(h => h.name === hotel.name)
          )
          .slice(0, 8) // Limit to 8 suggestions
        
        setFilteredHotels(matchingHotels)
      } else {
        setFilteredHotels([])
      }
    } catch (error) {
      // console.error('Error fetching hotels:', error)
      setFilteredHotels([])
    }
  }

  return (
    <div className="min-h-screen font-inter" style={{ background: 'linear-gradient(to bottom, #D9E4F8, #ffffff)' }}>
      {/* Header */}
      <AuthHeader />

      {/* Tab Navigation */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('flights')}
              className={'py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ' + (
                activeTab === 'flights'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              )}
            >
              <PaperAirplaneIcon className="w-5 h-5 inline mr-2" />
              FLIGHTS
            </button>
            <button
              onClick={() => setActiveTab('hotels')}
              className={'py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ' + (
                activeTab === 'hotels'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              )}
            >
              <BuildingOfficeIcon className="w-5 h-5 inline mr-2" />
              HOTELS
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Form */}
        <div className="py-8 sm:py-12 lg:py-20 rounded-lg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-6 sm:mb-8" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.03em', lineHeight: '1.3' }}>
              Enter Your Redemption To Check Against Real Traveler Data
            </h2>
            
            {activeTab === 'flights' ? (
              // Flight Search Form
              <div className="max-w-4xl mx-auto">

                {/* Common fields for all trip types */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
                  <div className="flex flex-col sm:col-span-2 lg:col-span-1 xl:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Loyalty Program <span className="text-gray-700">*</span>
                    </label>
                    <select
                      value={flightSearchCriteria.loyaltyProgram || ''}
                      onChange={(e) => setFlightSearchCriteria({...flightSearchCriteria, loyaltyProgram: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    >
                      <option value="">Select Program</option>
                      {loyaltyPrograms.map(program => (
                        <option key={program.id} value={program.id}>
                          {program.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Points Spent <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 25000"
                      value={flightSearchCriteria.points || ''}
                      onChange={(e) => setFlightSearchCriteria({...flightSearchCriteria, points: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Cash Price (USD)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        placeholder="e.g., 500.00 (optional)"
                        value={flightSearchCriteria.cashValue || ''}
                        onChange={(e) => {
                          setFlightSearchCriteria({...flightSearchCriteria, cashValue: e.target.value});
                          // Clear auto-fetch indicator when user manually changes the value
                          if (autoFetchedCashPrice) {
                            setAutoFetchedCashPrice(null);
                          }
                        }}
                        className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                      />
                      {autoFetchedCashPrice && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>Auto-fetched</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {autoFetchedCashPrice ? 
                        `Auto-fetched from Google Flights: $${autoFetchedCashPrice.fetched_cash_price}` : 
                        'Current market price (leave empty to auto-fetch from Google Flights)'
                      }
                    </p>
                  </div>
                </div>

                {/* Flight segments for all trip types */}
                <div className="space-y-4 mt-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">Flight Segments</h4>
                    {flightSearchCriteria.tripType === 'multi_city' && (
                      <button
                        type="button"
                        onClick={addSegment}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        Add Segment
                      </button>
                    )}
                  </div>
                  
                  {/* Flight Segment Container with tabs inside */}
                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    {/* Trip Type tabs */}
                    <div className="flex space-x-8 mb-6">
                      <button
                        onClick={() => setFlightSearchCriteria({...flightSearchCriteria, tripType: 'one_way'})}
                        className={`py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
                          flightSearchCriteria.tripType === 'one_way'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        One Way
                      </button>
                      <button
                        onClick={() => setFlightSearchCriteria({...flightSearchCriteria, tripType: 'round_trip'})}
                        className={`py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
                          flightSearchCriteria.tripType === 'round_trip'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Round Trip
                      </button>
                      <button
                        onClick={() => setFlightSearchCriteria({...flightSearchCriteria, tripType: 'multi_city'})}
                        className={`py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
                          flightSearchCriteria.tripType === 'multi_city'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Multi-City
                      </button>
                    </div>
                    
                    {flightSearchCriteria.segments.map((segment, index) => (
                      <div key={index} className={(flightSearchCriteria.tripType === 'round_trip' && index === 0) || (flightSearchCriteria.tripType === 'multi_city' && index < flightSearchCriteria.segments.length - 1) ? 'mb-8' : 'mb-4'}>
                        {(flightSearchCriteria.tripType === 'round_trip' || 
                          (flightSearchCriteria.tripType === 'multi_city' && flightSearchCriteria.segments.length > 1)) && (
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="text-sm font-medium text-gray-700">
                              {flightSearchCriteria.tripType === 'round_trip' 
                                ? (index === 0 ? 'Departure' : 'Return')
                                : 'Segment ' + (index + 1)
                              }
                            </h5>
                            {flightSearchCriteria.tripType === 'multi_city' && flightSearchCriteria.segments.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSegment(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Origin *
                            </label>
                            <input
                              type="text"
                              value={segment.origin_iata}
                              onChange={(e) => handleSegmentChange(index, 'origin_iata', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                              placeholder="LAX"
                              maxLength="3"
                            />
                          </div>
                          
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Destination *
                            </label>
                            <input
                              type="text"
                              value={segment.destination_iata}
                              onChange={(e) => handleSegmentChange(index, 'destination_iata', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                              placeholder="JFK"
                              maxLength="3"
                            />
                          </div>
                          
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Travel Date *
                            </label>
                            <input
                              type="date"
                              value={segment.travel_date}
                              min={index === 0 ? undefined : flightSearchCriteria.segments[index - 1]?.travel_date}
                              onChange={(e) => handleSegmentChange(index, 'travel_date', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Carrier
                            </label>
                            <input
                              type="text"
                              value={segment.operating_carrier_id}
                              onChange={(e) => handleSegmentChange(index, 'operating_carrier_id', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                              placeholder="AA (optional)"
                              maxLength="2"
                            />
                          </div>
                          
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Flight Number
                            </label>
                            <input
                              type="text"
                              value={segment.flight_number}
                              onChange={(e) => handleSegmentChange(index, 'flight_number', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                              placeholder="123 (optional)"
                            />
                          </div>
                          
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                              Cabin Class *
                            </label>
                            <select
                              value={segment.cabin_class}
                              onChange={(e) => handleSegmentChange(index, 'cabin_class', e.target.value)}
                              className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                            >
                              <option value="Economy">Economy</option>
                              <option value="Premium_Economy">Premium Economy</option>
                              <option value="Business">Business</option>
                              <option value="First">First</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Action Buttons - Show for all trip types */}
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 pt-8">
                  <button
                    onClick={handleFlightSearch}
                    disabled={loading}
                    className="w-full sm:w-auto px-8 sm:px-12 py-3 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    style={{ backgroundColor: '#548BCA' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4A7BB8'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#548BCA'}
                  >
                    {loading ? 'Searching...' : 'Submit'}
                  </button>
                  <button
                    onClick={resetForm}
                    className="w-full sm:w-auto px-8 sm:px-12 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium"
                  >
                    Reset
                  </button>
                </div>
              </div>
            ) : (
              // Hotel Search Form
              <div className="max-w-4xl mx-auto">
                {/* First row - Basic redemption details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
                  <div className="flex flex-col sm:col-span-2 lg:col-span-1 xl:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Loyalty Program <span className="text-gray-700">*</span>
                    </label>
                    <select
                      value={hotelSearchCriteria.loyaltyProgram || ''}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, loyaltyProgram: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    >
                      <option value="">Select Program</option>
                      {getFilteredPrograms('hotel').map(program => (
                        <option key={program.id} value={program.id}>
                          {program.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Points Spent <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 35000"
                      value={hotelSearchCriteria.points || ''}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, points: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Cash Price (USD)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 350.00 (optional)"
                      value={hotelSearchCriteria.cashPrice || ''}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, cashPrice: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                    <p className="text-xs text-gray-500 mt-1">Current market price</p>
                  </div>
                </div>

                {/* Second row - Hotel name, city, country, and dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6 mb-8">
                  <div className="flex flex-col sm:col-span-2 md:col-span-1 lg:col-span-2 xl:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Hotel Name <span className="text-gray-700">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={hotelSearchCriteria.hotelName}
                        onChange={(e) => handleHotelNameChange(e.target.value)}
                        onKeyDown={handleHotelKeyDown}
                        onFocus={() => {
                          if (hotelSearchCriteria.hotelName.trim().length >= 2) {
                            fetchHotels(hotelSearchCriteria.hotelName)
                            setShowHotelDropdown(true)
                          }
                        }}
                        onBlur={() => {
                          // Delay hiding dropdown to allow for clicks
                          setTimeout(() => setShowHotelDropdown(false), 200)
                        }}
                        placeholder="Type to search existing hotels..."
                        className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                      />
                      
                      {/* Hotel dropdown */}
                      {showHotelDropdown && filteredHotels.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredHotels.map((hotel, index) => (
                            <div
                              key={hotel.id}
                                                              className={'px-3 py-2 cursor-pointer text-sm ' + (
                                index === selectedHotelIndex 
                                  ? 'bg-primary-500 text-white' 
                                  : 'hover:bg-gray-100'
                              )}
                              onClick={() => handleHotelSelect(hotel)}
                              onMouseEnter={() => setSelectedHotelIndex(index)}
                            >
                              <div className="font-medium">{hotel.name}</div>
                              <div className="text-xs text-gray-500">
                                {hotel.city && hotel.country && `${hotel.city}, ${hotel.country}`}
                                {hotel.brand && hotel.city && ' • '}
                                {hotel.brand && hotel.brand}
                                {hotel.hotel_group && hotel.brand && ' • '}
                                {hotel.hotel_group && hotel.hotel_group}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col xl:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      City <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="text"
                      value={hotelSearchCriteria.city}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, city: e.target.value})}
                      placeholder="e.g., New York"
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                  
                  <div className="flex flex-col xl:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Country <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="text"
                      value={hotelSearchCriteria.country}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, country: e.target.value})}
                      placeholder="e.g., United States"
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Check-in Date <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="date"
                      value={hotelSearchCriteria.checkInDate}
                      onChange={(e) => {
                        const newCheckInDate = e.target.value
                        setHotelSearchCriteria({
                          ...hotelSearchCriteria, 
                          checkInDate: newCheckInDate,
                          // Reset check-out date if it's now before the new check-in date
                          checkOutDate: hotelSearchCriteria.checkOutDate <= newCheckInDate 
                            ? new Date(new Date(newCheckInDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                            : hotelSearchCriteria.checkOutDate
                        })
                      }}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      Check-out Date <span className="text-gray-700">*</span>
                    </label>
                    <input
                      type="date"
                      value={hotelSearchCriteria.checkOutDate}
                      min={hotelSearchCriteria.checkInDate}
                      onChange={(e) => setHotelSearchCriteria({...hotelSearchCriteria, checkOutDate: e.target.value})}
                      className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 h-12"
                    />
                  </div>
                </div>
                
                {/* Action buttons - centered */}
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <button
                    onClick={handleHotelSearch}
                    disabled={loading}
                    className="w-full sm:w-auto px-8 sm:px-10 py-3 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    style={{ backgroundColor: '#548BCA' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4A7BB8'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#548BCA'}
                  >
                    {loading ? 'Searching...' : 'Submit'}
                  </button>
                  <button
                    onClick={resetForm}
                    className="w-full sm:w-auto px-8 sm:px-10 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Main Content Section - Search Results or Redemption Reference */}
          {hasSearched ? (
            <div className="mt-8">
              {/* Combined Redemption Reference and Search Results */}
              {aggregatedData.totalRedemptions > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Redemption Reference</h3>
                  {/* First Row - Market Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Average CPP</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {aggregatedData.averageCpp.toFixed(2)}¢
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Total Redemptions</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {aggregatedData.totalRedemptions}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Mode Points</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {aggregatedData.modePoints.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Lowest Points</div>
                      <div className="text-2xl font-bold text-red-600">
                        {aggregatedData.lowestPoints.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  {/* Second Row - Your Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Your CPP</div>
                      <div className="text-2xl font-bold text-indigo-600">
                        {((parseFloat(activeTab === 'flights' ? flightSearchCriteria.cashValue : hotelSearchCriteria.cashPrice) / 
                          parseInt(activeTab === 'flights' ? flightSearchCriteria.points : hotelSearchCriteria.points)) * 100).toFixed(2)}¢
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Top % of 180d Redemptions</div>
                      <div className="text-2xl font-bold text-orange-600">
                        Top {Math.round(((aggregatedData.totalRedemptions - searchResults.filter(r => r.value_cpp > ((parseFloat(activeTab === 'flights' ? flightSearchCriteria.cashValue : hotelSearchCriteria.cashPrice) / parseInt(activeTab === 'flights' ? flightSearchCriteria.points : hotelSearchCriteria.points)) * 100)).length) / aggregatedData.totalRedemptions) * 100)}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Your Points</div>
                      <div className="text-2xl font-bold text-teal-600">
                        {parseInt(activeTab === 'flights' ? flightSearchCriteria.points : hotelSearchCriteria.points).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-600 mb-2">Cash Price</div>
                      <div className="text-2xl font-bold text-orange-600 flex items-center justify-center">
                        ${activeTab === 'flights' ? (flightSearchCriteria.cashValue ? parseFloat(flightSearchCriteria.cashValue).toFixed(0) : '0') : (hotelSearchCriteria.cashPrice ? parseFloat(hotelSearchCriteria.cashPrice).toFixed(0) : '0')}
                        {autoFetchedCashPrice && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Auto-fetched
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Search Results within the same container */}
                  {searchResults.length > 0 && (
                    <div className="space-y-4">
                      {searchResults.map((result, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-4 mb-2">
                                <span className="text-sm font-medium text-gray-600">
                                  {result.program?.name || 'Unknown Program'}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {new Date(result.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600">
                                {result.redemption_type === 'flight' ? (
                                  <>
                                    {result.flight?.segments?.[0]?.origin_iata} → {result.flight?.segments?.[0]?.destination_iata}
                                    {result.flight?.segments?.[1] && ` → ${result.flight?.segments?.[1]?.destination_iata}`}
                                    <span className="ml-2 font-medium text-gray-800">
                                      ${result.cash_price ? result.cash_price.toFixed(0) : '0'} cash
                                      {result.auto_fetched_cash_price && (
                                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                          <svg className="w-2.5 h-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          Auto-fetched
                                        </span>
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {result.hotel?.hotel?.name} - {result.hotel?.hotel?.city}, {result.hotel?.hotel?.country}
                                    <span className="ml-2 font-medium text-gray-800">
                                      ${result.cash_price ? result.cash_price.toFixed(0) : '0'} cash
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-gray-900">
                                {result.value_cpp.toFixed(2)}¢
                              </div>
                              <div className="text-sm text-gray-600">
                                {result.points_spent.toLocaleString()} pts
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No Results Message */}
              {searchResults.length === 0 && aggregatedData.totalRedemptions === 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">No Results Found</h3>
                  <p className="text-gray-600">
                    No comparable redemptions found for your search criteria. Try adjusting your search parameters.
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Show Redemption Reference (default view)
            <div className="mt-8">
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Redemption Reference</h3>
              
              {/* Top Loyalty Programs Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {globalStatsLoading ? (
                  // Loading skeleton
                  Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-3"></div>
                      <div className="h-6 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  ))
                ) : globalStats.topPrograms.length > 0 ? (
                  globalStats.topPrograms.map((program, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-lg font-semibold text-gray-900">{program.name}</h5>
                        <span className="text-sm text-gray-500">#{index + 1}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Avg CPP:</span>
                          <span className="text-lg font-semibold text-primary-600">{program.avgCpp.toFixed(1)}¢</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Redemptions:</span>
                          <span className="text-sm font-medium text-gray-900">{program.count}</span>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: globalStats.bestRecord > 0 ? (program.avgCpp / globalStats.bestRecord) * 100 + '%' : '0%' }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">
                          {globalStats.bestRecord > 0 ? ((program.avgCpp / globalStats.bestRecord) * 100).toFixed(0) + '% of best' : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  // No programs available
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 mb-2">No loyalty program data available</p>
                    <p className="text-sm text-gray-400">Add redemptions to see program statistics</p>
                  </div>
                )}
              </div>

              {/* Total Redemptions Info */}
              {!globalStatsLoading && globalStats.totalRedemptions > 0 && (
                <div className="text-center mt-8">
                  <p className="text-gray-600">
                    Based on <span className="font-semibold text-gray-900">{globalStats.totalRedemptions.toLocaleString()}</span> {activeTab === 'flights' ? 'flight' : 'hotel'} redemptions across all loyalty programs
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  )
}

export default CompareRedemption